import { notEmpty, theme } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { GlobalChecker } from "~/components/article/article-render/global-checker";
import {
  useMobileInlineAdSlotIsDisabledFlag,
  useReduceWhitespaceByAvailableSpace,
} from "~/components/article/article-render/hooks";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { section } from "~/data";
import type { genericArticleArticle$key } from "~/queries/__generated__/genericArticleArticle.graphql";

import {
  Author,
  AuthorDetail,
  CommentAndReaction,
  Content,
  Correction,
  DesktopHeaderAdSlot,
  Follow,
  GenericArticleContainer,
  Headline,
  Leading,
  Left,
  MobileContentFooterAdSlot,
  MobileContentHeaderAdSlot,
  MobileFooterAdSlot,
  MoreOnThis,
  RightAvailableSpaceTracker,
  Section,
  SpeechPlayer,
  Sponsor,
  StyledArticleContentRightSection,
  SubHeadline,
  TabletContentFooterAdSlot,
  Topic,
  TopPicksAdSlot,
  WidgetsBottom,
  WidgetsBottomAvailableSpaceTracker,
  WidgetsTop,
} from "./styles";

export type Props = {
  reference: genericArticleArticle$key;
} & ArticleRenderChildProps;

export const GenericArticle: FunctionComponent<Props> = ({
  isInlinePaywallDomChanged,
  isShowInlinePaywall,
  provider,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleArticle on Content {
        entityId
        sections {
          value {
            entityId
          }
        }
        ...globalCheckerContent
        ...reduceWhitespaceByAvailableSpaceArticle
      }
    `,
    reference,
  );

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    isEnabled: notEmpty(isShowInlinePaywall ?? isInlinePaywallDomChanged),
    reference: article,
    specs: {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    trackers: {
      right: RightAvailableSpaceTracker,
      widgetsBottom: WidgetsBottomAvailableSpaceTracker,
    },
  });

  const magazineVariant = useMemo(() => {
    const sectionEntityIds =
      first(article?.sections)?.value?.map(section => section?.entityId) ?? [];
    switch (true) {
      case sectionEntityIds.includes(section.postMagazine.entityId):
        return section.postMagazine.entityId;
      case sectionEntityIds.includes(section.thisWeekInAsia.entityId):
        return section.thisWeekInAsia.entityId;
    }
  }, [article?.sections]);

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();

  return (
    <GenericArticleContainer>
      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
        })}
      </DesktopHeaderAdSlot>
      <MobileContentHeaderAdSlot>
        {provider.adslot({
          adUnit: "m_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.only("mobile"),
          sizes: [[300, 250], "fluid"],
          withBackground: true,
          withLabel: true,
        })}
      </MobileContentHeaderAdSlot>
      <WidgetsTop>{provider.topicFollowWidget({ variant: "stack" })}</WidgetsTop>
      <WidgetsBottom>
        {provider.leftHandSideWidgets({
          topPicksWidgetProps: {
            maximumHeight: reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.maximumHeight,
            slots: {
              footer: {
                estimatedHeight: 260,
                node: (
                  <TopPicksAdSlot>
                    {provider.adslot({
                      adUnit: "d_native3b",
                      breakpoint: theme.breakpoints.up("desktop"),
                      sizes: ["fluid"],
                    })}
                  </TopPicksAdSlot>
                ),
              },
              header: {
                estimatedHeight: 260,
                node: (
                  <TopPicksAdSlot>
                    {provider.adslot({
                      adUnit: "d_native2b",
                      breakpoint: theme.breakpoints.up("desktop"),
                      fluidDefaultSize: {
                        height: 260,
                      },
                      sizes: ["fluid"],
                    })}
                  </TopPicksAdSlot>
                ),
              },
            },
          },
        })}
      </WidgetsBottom>
      {reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.trackerNode}

      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "hidden",
            tabletUp: "show",
          },
          responsiveImageVariants: { desktopUp: "normal", mobileUp: "square", tabletUp: "normal" },
          responsiveVariants: {
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["768x768"],
            tabletUp: ImageSize["1020x680"],
          },
        })}
      </Leading>
      <Section>{provider.sections()}</Section>
      <Headline $magazineVariant={magazineVariant}>{provider.headline()}</Headline>
      <SubHeadline>{provider.subHeadline()}</SubHeadline>
      <Sponsor>{provider.sponsor()}</Sponsor>
      <Follow>{provider.topicFollowWidget({ variant: "banner" })}</Follow>
      <Author>{provider.authorBlock()}</Author>
      <Correction>{provider.correction()}</Correction>
      <SpeechPlayer>{provider.speechPlayer({})}</SpeechPlayer>
      <Content>
        {provider.body({
          inlineAdSlots: {
            "0": {
              adUnit: "outstream1",
            },
            "1": {
              adUnit: "m_lrec1",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
            "2": {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
            "3": {
              adUnit: "m_lrec4d",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
            "4": {
              adUnit: "m_lrec4e",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
            "5": {
              adUnit: "m_lrec4f",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
            "6": {
              adUnit: "native5",
              isDisabled: isInlineAdSlotDisabled,
            },
          },
        })}
      </Content>
      <CommentAndReaction>{provider.commentTriggerAndReaction()}</CommentAndReaction>
      <MoreOnThis>{provider.moreOnThis({ variant: "horizontal" })}</MoreOnThis>
      <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
      <Topic>{provider.topics()}</Topic>
      <Left $stickyStackedHeight={appBarStickyHeight}>
        {provider.shareWidget()}
        {provider.actionWidget()}
      </Left>
      <StyledArticleContentRightSection
        {...reduceWhitespaceByAvailableSpaceSpec.right}
        entityId={article.entityId}
        provider={provider}
      />
      {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}
      <ClientSideSuspense>
        <GlobalChecker reference={article}>
          <TabletContentFooterAdSlot>
            {provider.adslot({
              adUnit: "native4",
              breakpoint: theme.breakpoints.only("tablet"),
              sizes: ["fluid"],
            })}
          </TabletContentFooterAdSlot>
          <MobileContentFooterAdSlot>
            {provider.adslot({
              adUnit: "native4",
              breakpoint: theme.breakpoints.only("mobile"),
              sizes: ["fluid"],
            })}
          </MobileContentFooterAdSlot>
          <MobileFooterAdSlot>
            {provider.adslot({
              adUnit: "m_lrec3a",
              breakpoint: theme.breakpoints.only("mobile"),
              sizes: [[300, 250], "fluid"],
              withBackground: true,
              withLabel: true,
            })}
          </MobileFooterAdSlot>
        </GlobalChecker>
      </ClientSideSuspense>
    </GenericArticleContainer>
  );
};

GenericArticle.displayName = "GenericArticle";
